import { forwardRef, useCallback, useLayoutEffect, useRef, type ComponentPropsWithoutRef } from 'react'

interface GrowingTextareaProps extends Omit<ComponentPropsWithoutRef<'textarea'>, 'ref'> {
  /** Where the box stops growing and starts scrolling. */
  maxHeight: number
  /** Enter (without shift) asks the card to advance or submit. */
  onSubmitRequest?: () => void
}

/**
 * The free-text field inside an ask-user card. A textarea rather than an input
 * because what we ask for is a sentence, and a single-line field that scrolls
 * sideways makes a two-clause answer unreadable while typing. Starts at its
 * `rows` height, grows with the content, then scrolls.
 */
export const GrowingTextarea = forwardRef<HTMLTextAreaElement, GrowingTextareaProps>(function GrowingTextarea(
  { maxHeight, onSubmitRequest, value, onKeyDown, ...props },
  forwardedRef,
) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const assignTextareaRef = useCallback(
    (element: HTMLTextAreaElement | null) => {
      textareaRef.current = element

      if (typeof forwardedRef === 'function') {
        forwardedRef(element)
      } else if (forwardedRef) {
        forwardedRef.current = element
      }
    },
    [forwardedRef],
  )

  useLayoutEffect(() => {
    const el = textareaRef.current
    if (!el) return
    // Collapse before measuring, or `scrollHeight` just reports the height
    // already set and the box can never shrink back.
    el.style.height = 'auto'
    // An empty box is left to `rows`: the first pass can run before the
    // stylesheet settles, and that measurement pinned as an inline height
    // leaves the field several lines tall for the life of the card.
    if (value && el.scrollHeight > 0) {
      el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`
    }
  }, [value, maxHeight])

  return (
    <textarea
      ref={assignTextareaRef}
      value={value}
      onKeyDown={(e) => {
        // Enter submits, shift+Enter breaks the line. Stop propagation so the
        // key can't reach a parent/window cmd+enter handler (e.g. staged-action
        // invoke in the drawer — same bug class as HIG-5238).
        if (onSubmitRequest && e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault()
          e.stopPropagation()
          onSubmitRequest()
        }
        onKeyDown?.(e)
      }}
      {...props}
    />
  )
})
